import React from 'react';
import { FileTextIcon, TruckIcon, CalendarIcon, BuildingIcon, MapPinIcon, DollarSignIcon, UserIcon } from 'lucide-react';
import { PurchaseOrder } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
import { StatusBadge } from '../StatusBadge';
interface ReceiptHeaderProps {
  purchaseOrder: PurchaseOrder;
  locale?: string;
}
export function ReceiptHeader({
  purchaseOrder,
  locale = 'en-US'
}: ReceiptHeaderProps) {
  // Get status badge type based on PO status
  const getStatusBadgeType = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'confirmed':
        return 'success';
      case 'pending approval':
      case 'pending confirmation':
        return 'pending';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14541">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14542">
        <div className="flex justify-between items-center" data-id="element-14543">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14544">
            <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14545" />
            Purchase Order Details
          </h3>
          <StatusBadge status={getStatusBadgeType(purchaseOrder.status)} text={purchaseOrder.status} size="md" data-id="element-14546" />
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-14547">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-14548">
          <div data-id="element-14549">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14550">
              Purchase Order Information
            </h4>
            <div className="space-y-3" data-id="element-14551">
              <div className="flex items-start" data-id="element-14552">
                <div className="flex-shrink-0" data-id="element-14553">
                  <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-14554" />
                </div>
                <div className="ml-3" data-id="element-14555">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14556">
                    {purchaseOrder.id}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14557">{purchaseOrder.name}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14558">
                <div className="flex-shrink-0" data-id="element-14559">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-14560" />
                </div>
                <div className="ml-3" data-id="element-14561">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14562">
                    Order Date
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14563">{purchaseOrder.date}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14564">
                <div className="flex-shrink-0" data-id="element-14565">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-14566" />
                </div>
                <div className="ml-3" data-id="element-14567">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14568">
                    Total Value
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14569">
                    {formatCurrency(purchaseOrder.total, purchaseOrder.currency, locale)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14570">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14571">
              Vendor Information
            </h4>
            <div className="space-y-3" data-id="element-14572">
              <div className="flex items-start" data-id="element-14573">
                <div className="flex-shrink-0" data-id="element-14574">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-14575" />
                </div>
                <div className="ml-3" data-id="element-14576">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14577">Vendor</p>
                  <p className="text-sm text-gray-500" data-id="element-14578">
                    {purchaseOrder.vendor}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14579">
                <div className="flex-shrink-0" data-id="element-14580">
                  <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-14581" />
                </div>
                <div className="ml-3" data-id="element-14582">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14583">
                    Delivery Status
                  </p>
                  <div className="mt-1" data-id="element-14584">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-14585">
                      Partially Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14586">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14587">
              Shipping Information
            </h4>
            <div className="space-y-3" data-id="element-14588">
              <div className="flex items-start" data-id="element-14589">
                <div className="flex-shrink-0" data-id="element-14590">
                  <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-14591" />
                </div>
                <div className="ml-3" data-id="element-14592">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14593">Entity</p>
                  <p className="text-sm text-gray-500" data-id="element-14594">
                    {purchaseOrder.entity}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14595">
                <div className="flex-shrink-0" data-id="element-14596">
                  <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-14597" />
                </div>
                <div className="ml-3" data-id="element-14598">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14599">Site</p>
                  <p className="text-sm text-gray-500" data-id="element-14600">{purchaseOrder.site}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}